import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Cycle} from '@services/model/cycle';
import {ConfigService} from '@services/infra/config.service';
import {CycleResource} from '@services/resource/cycle-resource';
import {map, timeout} from 'rxjs/operators';
import {ClientConfig} from '@app/client-config';
import {ErrorService} from '@services/error.service';
import moment from 'moment';
import {Option} from 'fp-ts/lib/Option';
import {head} from 'fp-ts/lib/Array';
import {UUID} from '@util/types';

@Injectable({
  providedIn: 'root',
})
export class CycleService {
  constructor(
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
    private readonly errors: ErrorService,
  ) {}

  getAll(filterByApplicationInterval: boolean): Observable<Cycle[]> {
    const url = `${this.conf.backendUrl}/v1/cycle?filterByApplicationInterval=${filterByApplicationInterval}`;
    return this.http.get<CycleResource[]>(url).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((cr) => cr.map(Cycle.fromResource)),
    );
  }

  getById(cycleId: UUID): Observable<Cycle> {
    const url = `${this.conf.backendUrl}/v1/cycle/${cycleId}`;
    return this.http.get<CycleResource>(url).pipe(map((cr) => Cycle.fromResource(cr)));
  }

  getCurrentVotingCycle(): Observable<Option<Cycle>> {
    const now = moment(new Date());
    return this.getAll(false).pipe(map((cs: Cycle[]) => head(cs.filter((c) => now.isBetween(c.votingStart, c.votingEnd)))));
  }

  create(c: Cycle): Observable<Cycle> {
    const url = `${this.conf.backendUrl}/v1/cycle`;
    return this.http.post<CycleResource>(url, Cycle.toResource(c)).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler(), map(Cycle.fromResource));
  }

  update(c: Cycle): Observable<Cycle> {
    const url = `${this.conf.backendUrl}/v1/cycle/${c.uuid}`;
    return this.http.put<CycleResource>(url, Cycle.toResource(c)).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler(), map(Cycle.fromResource));
  }

  createOrUpdate(c: Cycle): Observable<Cycle> {
    return c.uuid ? this.update(c) : this.create(c);
  }

  delete(uuid: string): Observable<void> {
    const url = `${this.conf.backendUrl}/v1/cycle/${uuid}`;
    return this.http.delete<void>(url).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }
}
